import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  MagnifyingGlass, 
  Rocket, 
  Globe, 
  BookOpen,
  Users,
  CheckCircle,
  FileText,
  ClipboardText,
  PencilSimple,
  Newspaper
} from "@phosphor-icons/react";
import axios from "axios";
import { DEFAULT_JOURNALS } from "@/lib/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const heroSlides = [
  {
    image: "https://images.pexels.com/photos/3735715/pexels-photo-3735715.jpeg?auto=compress&cs=tinysrgb&w=1920",
    title: "Advancing Medical & Scientific Discovery",
    subtitle: "iScience Publishers is dedicated to delivering high-impact, peer-reviewed research to a global audience, ensuring academic excellence and innovation."
  },
  {
    image: "https://images.pexels.com/photos/6129499/pexels-photo-6129499.jpeg?auto=compress&cs=tinysrgb&w=1920",
    title: "Global Research Visibility",
    subtitle: "Publish with integrity and reach researchers, clinicians, and scholars worldwide through our indexed journals."
  }
];

const whyChooseUs = [
  {
    icon: MagnifyingGlass,
    title: "Rigorous Peer Review",
    description: "Every manuscript undergoes thorough evaluation by expert reviewers ensuring scientific integrity."
  },
  {
    icon: Rocket,
    title: "Rapid Publication",
    description: "Our streamlined process ensures your research reaches the community quickly without compromising quality."
  },
  {
    icon: Globe,
    title: "Global Visibility",
    description: "Indexed in major databases for maximum discoverability and citation potential worldwide."
  },
  {
    icon: BookOpen,
    title: "Open Access Options",
    description: "Flexible publishing models including open access for broader research dissemination."
  },
  {
    icon: Users,
    title: "Expert Editorial Board",
    description: "Distinguished scholars and researchers guiding editorial standards and scientific excellence."
  },
  {
    icon: CheckCircle,
    title: "Publication Ethics",
    description: "Committed to the highest standards of publication ethics and research integrity."
  }
];

const submissionSteps = [
  { number: 1, icon: FileText, title: "Submit", description: "Upload your manuscript" },
  { number: 2, icon: MagnifyingGlass, title: "Review", description: "Peer review process" },
  { number: 3, icon: PencilSimple, title: "Revise", description: "Address feedback" },
  { number: 4, icon: Newspaper, title: "Publish", description: "Global publication" }
];

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [journals, setJournals] = useState(DEFAULT_JOURNALS);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchJournals = async () => {
      try {
        const response = await axios.get(`${API}/journals`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setJournals(response.data);
        } else {
          setJournals(DEFAULT_JOURNALS);
        }
      } catch (error) {
        console.error("Failed to fetch journals:", error);
        setJournals(DEFAULT_JOURNALS);
      }
    };
    fetchJournals();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative h-[600px] sm:h-[700px] overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/60 to-transparent" />
          </div>
        ))}
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-2xl">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold mb-4">
                Medical & Scientific Publishing
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Chivo'] tracking-tight leading-none mb-6">
                {heroSlides[currentSlide].title}
              </h1>
              <p className="text-lg text-[#CBD5E1] leading-relaxed mb-8">
                {heroSlides[currentSlide].subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/submit-manuscript"
                  data-testid="hero-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white px-6 py-3 rounded-sm font-medium transition-colors"
                >
                  Submit Manuscript
                  <ArrowRight size={18} weight="bold" />
                </Link>
                <Link
                  to="/journals"
                  data-testid="hero-explore-btn"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white/10 px-6 py-3 rounded-sm font-medium transition-colors"
                >
                  Explore Journals
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              data-testid={`hero-slide-indicator-${index}`}
              className={`w-12 h-1 rounded-full transition-colors ${
                index === currentSlide ? "bg-[#0D9488]" : "bg-white/30"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* About Section */}
      <section data-testid="about-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
                About iScience Publishers
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4 mb-6">
                Empowering Global Scientific Research
              </h2>
              <p className="text-[#334155] leading-relaxed mb-6">
                iScience Publishers is an international academic publishing platform committed to 
                advancing scientific and medical knowledge. We provide a robust and transparent 
                peer-review system that ensures the highest standards of research integrity.
              </p>
              <p className="text-[#334155] leading-relaxed mb-8">
                Our mission is to empower researchers, clinicians, and scholars by offering a 
                seamless publishing experience, global visibility, and rapid dissemination of 
                knowledge that drives innovation in healthcare and life sciences.
              </p>
              <Link
                to="/about"
                data-testid="about-learn-more-btn"
                className="inline-flex items-center gap-2 text-[#0D9488] font-semibold hover:text-[#0F766E] transition-colors"
              >
                Learn More About Us
                <ArrowRight size={18} weight="bold" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/3735711/pexels-photo-3735711.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Scientific Research"
                className="w-full rounded-sm shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0D9488] text-white p-6 rounded-sm">
                <div className="text-4xl font-bold font-['Chivo']">25+</div>
                <div className="text-sm opacity-90">Years of Excellence</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Journals Section */}
      <section data-testid="journals-section" className="py-20 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Our Publications
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4">
              Featured Journals
            </h2>
            <p className="text-[#64748B] mt-4 max-w-2xl mx-auto">
              Each journal is curated with strict editorial standards and indexed for global accessibility.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {journals.map((journal, index) => (
              <motion.div
                key={journal.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`journal-card-${journal.id}`}
                className="bg-white border border-[#E2E8F0] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 rounded-sm flex flex-col h-full group"
              >
                <div className="aspect-[4/3] mb-4 overflow-hidden rounded-sm">
                  <img
                    src={journal.image_url}
                    alt={journal.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h3 className="font-semibold text-[#0F172A] font-['Chivo'] mb-2">
                  {journal.title}
                </h3>
                <p className="text-sm text-[#64748B] mb-4 flex-grow">
                  {journal.description}
                </p>
                <div className="flex items-center justify-between text-xs text-[#94A3B8] border-t border-[#E2E8F0] pt-4">
                  <span>Frequency: {journal.frequency || "Monthly"}</span>
                  <span>IF: {journal.impact_factor}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/journals"
              data-testid="view-all-journals-btn"
              className="inline-flex items-center gap-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white px-6 py-3 rounded-sm font-medium transition-colors"
            >
              View All Journals
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="why-choose-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Our Commitment
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4">
              Why Choose iScience Publishers
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`why-choose-${index}`}
                className="bg-[#F8FAFC] border-l-4 border-[#0D9488] p-6 rounded-r-sm"
              >
                <item.icon size={32} className="text-[#0D9488] mb-4" weight="duotone" />
                <h3 className="font-semibold text-[#0F172A] font-['Chivo'] mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-[#64748B]">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Submission Process Section */}
      <section data-testid="submission-process-section" className="py-20 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Simple Process
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4">
              Publication Journey
            </h2>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-0">
            {submissionSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.15 }}
                data-testid={`submission-step-${step.number}`}
                className="flex items-center"
              >
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-white border-2 border-[#0D9488] flex items-center justify-center mx-auto mb-4">
                    <step.icon size={32} className="text-[#0D9488]" weight="duotone" />
                  </div>
                  <div className="text-xs text-[#0D9488] font-bold uppercase tracking-wider mb-1">
                    Step {step.number}
                  </div>
                  <div className="font-semibold text-[#0F172A] font-['Chivo']">
                    {step.title}
                  </div>
                  <div className="text-sm text-[#64748B]">
                    {step.description}
                  </div>
                </div>
                {index < submissionSteps.length - 1 && (
                  <div className="hidden md:block w-24 h-0.5 bg-gradient-to-r from-[#0D9488] to-[#E2E8F0] mx-4" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section data-testid="cta-section" className="py-20 md:py-24 bg-gradient-to-r from-[#0D9488] to-[#1E40AF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Chivo'] tracking-tight mb-4">
              Ready to Share Your Research?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Submit your manuscript today and contribute to the advancement of global scientific knowledge.
            </p>
            <Link
              to="/submit-manuscript"
              data-testid="cta-submit-btn"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] hover:bg-[#F0FDFA] px-8 py-4 rounded-sm font-semibold transition-colors"
            >
              Submit Your Manuscript
              <ArrowRight size={20} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
